import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
    printPerformanceReport,
    printDailyClosing,
    printMonthlyMatrix,
    printFinancialAudit,
    money
} from '../utils/reportPrint';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, Filler);

const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function UnifiedReports() {
    const { authFetch, selectedDepo, user, depos, systemSettings } = useContext(AppContext);
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // ── Tab Management (Max 3 Tabs) ──────────────────────────────────────────
    // Tab 0: Executive & Multi-Branch Performance (/reports/performance)
    // Tab 1: Daily Closing & Monthly Matrix (/reports/closing-matrix)
    // Tab 2: Financial Audit & Alerts (/reports/audit-alerts)
    const getTabFromPath = useCallback(() => {
        const p = location.pathname;
        if (p.includes('closing') || p.includes('matrix') || p.includes('eod')) return 1;
        if (p.includes('audit') || p.includes('alerts') || p.includes('financial') || p.includes('expiry')) return 2;
        const qTab = searchParams.get('tab');
        if (qTab === 'daily-monthly' || qTab === 'matrix' || qTab === 'eod') return 1;
        if (qTab === 'audit-alerts' || qTab === 'audit' || qTab === 'alerts' || qTab === 'financial') return 2;
        return 0;
    }, [location.pathname, searchParams]);

    const [activeTab, setActiveTab] = useState(getTabFromPath);

    useEffect(() => {
        setActiveTab(getTabFromPath());
    }, [getTabFromPath]);

    const switchTab = (idx) => {
        setActiveTab(idx);
        const paths = ['/reports/performance', '/reports/closing-matrix', '/reports/audit-alerts'];
        navigate(paths[idx]);
    };

    // ── Common State ────────────────────────────────────────────────────────
    const resolvedDepo = selectedDepo === 'Global' ? 'All' : selectedDepo;
    const branchName = selectedDepo === 'Global' ? t('All Branches') : (depos?.find(d => String(d.id) === String(selectedDepo))?.name || `Branch ${selectedDepo}`);
    const preparedBy = user?.full_name || user?.name || user?.username || 'Finance Dept';

    // ═════════════════════════════════════════════════════════════════════════
    // TAB 1: EXECUTIVE & MULTI-BRANCH PERFORMANCE STATE
    // ═════════════════════════════════════════════════════════════════════════
    const now = new Date();
    const [dateRangeType, setDateRangeType] = useState('This Month');
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');
    const [branchFilter, setBranchFilter] = useState(resolvedDepo);
    const [perfData, setPerfData] = useState(null);
    const [biData, setBiData] = useState(null);
    const [analyticsData, setAnalyticsData] = useState(null);
    const [perfLoading, setPerfLoading] = useState(false);
    const [perfError, setPerfError] = useState(null);

    // Sync branchFilter with top global selector if user changes it
    useEffect(() => {
        if (selectedDepo === 'Global') setBranchFilter('All');
        else setBranchFilter(selectedDepo);
    }, [selectedDepo]);

    const getPerfDateBounds = useCallback(() => {
        const current = new Date();
        if (dateRangeType === 'This Month') {
            const s = new Date(current.getFullYear(), current.getMonth(), 1).toISOString().split('T')[0];
            const e = new Date(current.getFullYear(), current.getMonth() + 1, 0).toISOString().split('T')[0];
            return { start: s, end: e, label: `${MONTH_NAMES[current.getMonth()]} ${current.getFullYear()}` };
        }
        if (dateRangeType === 'This Year') {
            return { start: `${current.getFullYear()}-01-01`, end: `${current.getFullYear()}-12-31`, label: `Year ${current.getFullYear()}` };
        }
        return {
            start: customStart || new Date(current.getFullYear(), current.getMonth(), 1).toISOString().split('T')[0],
            end: customEnd || current.toISOString().split('T')[0],
            label: `${customStart || 'Start'} to ${customEnd || 'End'}`
        };
    }, [dateRangeType, customStart, customEnd]);

    const parseSafeJson = async (res) => {
        if (!res) return null;
        try {
            const text = await res.text();
            if (!res.ok) {
                console.warn('API non-ok response:', res.status, text.slice(0, 150));
                return null;
            }
            return JSON.parse(text);
        } catch (err) {
            console.warn('Failed parsing JSON response:', err);
            return null;
        }
    };

    const fetchPerformance = useCallback(async () => {
        setPerfLoading(true);
        setPerfError(null);
        const { start, end } = getPerfDateBounds();
        try {
            const [mbaRes, biRes, anaRes] = await Promise.all([
                authFetch(`/api/multi_branch_analysis?start_date=${start}&end_date=${end}&branch_id=${branchFilter}`).catch(e => {
                    console.warn('multi_branch_analysis fetch error:', e);
                    return null;
                }),
                authFetch('/api/business_analysis').catch(e => {
                    console.warn('business_analysis fetch error:', e);
                    return null;
                }),
                authFetch('/api/analytics?type=dashboard').catch(e => {
                    console.warn('analytics fetch error:', e);
                    return null;
                }),
            ]);

            const [mbaJson, biJson, anaJson] = await Promise.all([
                parseSafeJson(mbaRes),
                parseSafeJson(biRes),
                parseSafeJson(anaRes),
            ]);

            setPerfData(mbaJson);
            setBiData(biJson);
            setAnalyticsData(anaJson);

            if (!mbaJson && !biJson && !anaJson) {
                setPerfError('Failed to load performance data from server.');
            }
        } catch (e) {
            console.error('Failed to load performance data:', e);
            setPerfError(e.message || 'Error loading performance data');
        } finally {
            setPerfLoading(false);
        }
    }, [authFetch, branchFilter, getPerfDateBounds]);

    useEffect(() => {
        if (activeTab === 0) {
            fetchPerformance();
        }
    }, [activeTab, fetchPerformance]);

    // ═════════════════════════════════════════════════════════════════════════
    // TAB 2: DAILY CLOSING & MONTHLY MATRIX STATE
    // ═════════════════════════════════════════════════════════════════════════
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(now.getFullYear());
    const [matrixRows, setMatrixRows] = useState([]);
    const [matrixTotals, setMatrixTotals] = useState({});
    const [matrixLoading, setMatrixLoading] = useState(false);

    // EOD Day Closing & Drawer state
    const [closureStatus, setClosureStatus] = useState('Open');
    const [closureSummary, setClosureSummary] = useState(null);
    const [dailyExpenses, setDailyExpenses] = useState([]);
    const [dailyPurchases, setDailyPurchases] = useState([]);
    const [closeModalOpen, setCloseModalOpen] = useState(false);
    const [expenseModalOpen, setExpenseModalOpen] = useState(false);
    const [actualCashCount, setActualCashCount] = useState('');
    const [closingNote, setClosingNote] = useState('');
    const [closingSubmitting, setClosingSubmitting] = useState(false);
    const [expenseForm, setExpenseForm] = useState({ category: '', amount: '', description: '', payment_source: 'Daily Cash Drawer' });

    const activeDepoId = selectedDepo === 'Global' ? (user?.depo_id || 1) : selectedDepo;

    const fetchDailyAndMatrix = useCallback(async () => {
        setMatrixLoading(true);

        try {
            const [matrixRes, closureRes, expRes, purchRes] = await Promise.all([
                authFetch(`/api/executive_dashboard?month=${selectedMonth}&year=${selectedYear}&date=${selectedDate}&depo_id=${activeDepoId}`).catch(() => null),
                authFetch(`/api/closure?date=${selectedDate}&depo_id=${activeDepoId}`).catch(() => null),
                authFetch(`/api/expenses?date=${selectedDate}&depo_id=${activeDepoId}`).catch(() => null),
                authFetch(`/api/purchases?date=${selectedDate}&depo_id=${activeDepoId}&per_page=100`).catch(() => null),
            ]);

            const [mJson, cJson, eJson, pJson] = await Promise.all([
                parseSafeJson(matrixRes),
                parseSafeJson(closureRes),
                parseSafeJson(expRes),
                parseSafeJson(purchRes),
            ]);

            if (mJson) {
                setMatrixRows(mJson.section2_matrix?.rows || []);
                setMatrixTotals(mJson.section2_matrix?.monthly_totals || {});
            }

            if (cJson) {
                setClosureStatus(cJson.status || 'Open');
                setClosureSummary(cJson.data || null);
            }

            if (eJson) {
                setDailyExpenses(Array.isArray(eJson.expenses) ? eJson.expenses : (Array.isArray(eJson) ? eJson : []));
            }

            if (pJson) {
                setDailyPurchases(Array.isArray(pJson.data) ? pJson.data : (Array.isArray(pJson) ? pJson : []));
            }
        } catch (e) {
            console.error('Failed to load daily/matrix data:', e);
        } finally {
            setMatrixLoading(false);
        }
    }, [authFetch, selectedMonth, selectedYear, selectedDate, activeDepoId]);

    useEffect(() => {
        if (activeTab === 1) {
            fetchDailyAndMatrix();
        }
    }, [activeTab, fetchDailyAndMatrix]);

    // Handle Close Day
    const handleConfirmCloseDay = async (e) => {
        e.preventDefault();
        setClosingSubmitting(true);
        try {
            const expectedCash = closureSummary?.expected_cash ?? (closureSummary?.cash_total - (closureSummary?.expense_total || 0));
            const actual = parseFloat(actualCashCount) || 0;
            const diff = actual - expectedCash;

            const res = await authFetch('/api/closure', {
                method: 'POST',
                body: JSON.stringify({
                    closure_date: selectedDate,
                    depo_id: activeDepoId,
                    expected_cash: expectedCash,
                    actual_cash: actual,
                    cash_difference: diff,
                    difference_reason: closingNote,
                    walkin_total: closureSummary?.walkin_total || 0,
                    cash_total: closureSummary?.cash_total || 0,
                    card_total: closureSummary?.card_total || 0,
                    account_credit_total: closureSummary?.account_credit_total || 0,
                    expense_total: closureSummary?.expense_total || 0,
                    achat_total: closureSummary?.achat_total || 0,
                    gross_sales: closureSummary?.gross_sales || 0,
                    net_sales: closureSummary?.net_sales || 0,
                    invoice_count: closureSummary?.invoice_count || 0,
                }),
            });
            const json = await res.json();
            if (res.ok) {
                setCloseModalOpen(false);
                setActualCashCount('');
                setClosingNote('');
                fetchDailyAndMatrix();
            } else {
                alert(json.message || 'Failed to close day');
            }
        } catch (err) {
            alert('Error closing day');
        } finally {
            setClosingSubmitting(false);
        }
    };

    // Handle Log Expense
    const handleLogExpense = async (e) => {
        e.preventDefault();
        try {
            const res = await authFetch('/api/expenses', {
                method: 'POST',
                body: JSON.stringify({
                    category: expenseForm.category,
                    amount: parseFloat(expenseForm.amount),
                    description: expenseForm.description,
                    date: selectedDate,
                    depo_id: activeDepoId,
                    payment_method: 'Cash',
                }),
            });
            if (res.ok) {
                setExpenseModalOpen(false);
                setExpenseForm({ category: '', amount: '', description: '', payment_source: 'Daily Cash Drawer' });
                fetchDailyAndMatrix();
            } else {
                alert('Failed to log expense');
            }
        } catch (err) {
            alert('Network error logging expense');
        }
    };

    // ═════════════════════════════════════════════════════════════════════════
    // TAB 3: FINANCIAL AUDIT & EXPIRY ALERTS STATE
    // ═════════════════════════════════════════════════════════════════════════
    const firstDayCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const todayStr = now.toISOString().split('T')[0];
    const [auditStart, setAuditStart] = useState(firstDayCurrentMonth);
    const [auditEnd, setAuditEnd] = useState(todayStr);
    const [auditData, setAuditData] = useState(null);
    const [expiryItems, setExpiryItems] = useState([]);
    const [expiryDays, setExpiryDays] = useState('60');
    const [auditLoading, setAuditLoading] = useState(false);

    const fetchAuditAndAlerts = useCallback(async () => {
        setAuditLoading(true);
        try {
            const [finRes, expRes] = await Promise.all([
                authFetch(`/api/financial_summary?start_date=${auditStart}&end_date=${auditEnd}`).catch(() => null),
                authFetch(`/api/reports?type=expiry&days=${expiryDays}`).catch(() => null),
            ]);

            const [finJson, expJson] = await Promise.all([
                parseSafeJson(finRes),
                parseSafeJson(expRes),
            ]);

            if (finJson) {
                setAuditData(finJson);
            }
            if (expJson) {
                setExpiryItems(Array.isArray(expJson) ? expJson : []);
            }
        } catch (e) {
            console.error('Failed to load audit/alerts:', e);
        } finally {
            setAuditLoading(false);
        }
    }, [authFetch, auditStart, auditEnd, expiryDays]);

    useEffect(() => {
        if (activeTab === 2) {
            fetchAuditAndAlerts();
        }
    }, [activeTab, fetchAuditAndAlerts]);

    // Expiry counts
    const expiryCounts = useMemo(() => {
        return {
            expired: expiryItems.filter(i => parseInt(i.days_until_expiry, 10) <= 0).length,
            critical: expiryItems.filter(i => {
                const d = parseInt(i.days_until_expiry, 10);
                return d > 0 && d <= 7;
            }).length,
            soon: expiryItems.filter(i => {
                const d = parseInt(i.days_until_expiry, 10);
                return d > 7 && d <= 30;
            }).length,
            watch: expiryItems.filter(i => parseInt(i.days_until_expiry, 10) > 30).length,
        };
    }, [expiryItems]);

    // ── Print Handlers ───────────────────────────────────────────────────────
    const handlePrintTab1 = () => {
        const bounds = getPerfDateBounds();
        const activeBranch = branchFilter === 'All' ? t('All Branches') : (depos?.find(d => String(d.id) === String(branchFilter))?.name || `Branch ${branchFilter}`);
        printPerformanceReport({
            settings: systemSettings,
            preparedBy,
            periodLabel: bounds.label,
            periodRange: `${bounds.start} to ${bounds.end}`,
            branchLabel: activeBranch,
            mba: perfData,
            bi: biData,
            analytics: analyticsData,
        });
    };

    const handlePrintDailyClosing = () => {
        const bName = depos?.find(d => String(d.id) === String(activeDepoId))?.name || `Branch ${activeDepoId}`;
        printDailyClosing({
            settings: systemSettings,
            preparedBy,
            branchName: bName,
            date: selectedDate,
            status: closureStatus,
            summary: closureSummary,
            expenses: dailyExpenses,
            purchases: dailyPurchases,
        });
    };

    const handlePrintMonthlyMatrix = () => {
        const bName = depos?.find(d => String(d.id) === String(activeDepoId))?.name || `Branch ${activeDepoId}`;
        printMonthlyMatrix({
            settings: systemSettings,
            preparedBy,
            branchName: bName,
            monthLabel: `${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}`,
            rows: matrixRows,
            totals: matrixTotals,
        });
    };

    const handlePrintAudit = () => {
        printFinancialAudit({
            settings: systemSettings,
            preparedBy,
            startDate: auditStart,
            endDate: auditEnd,
            fin: auditData,
            expiry: expiryItems,
            expiryDays,
            includeFinance: true,
        });
    };

    // ── Forecast chart for Tab 1 ─────────────────────────────────────────────
    const forecastChartData = useMemo(() => {
        if (!analyticsData?.historical?.length) return null;
        const labels = [];
        const histData = [];
        const predData = [];

        analyticsData.historical.forEach(h => {
            labels.push(h.date);
            histData.push(parseFloat(h.daily_revenue));
            predData.push(null);
        });

        if (analyticsData.forecast?.length) {
            predData[predData.length - 1] = histData[histData.length - 1];
            analyticsData.forecast.forEach(f => {
                labels.push(f.date);
                histData.push(null);
                predData.push(parseFloat(f.predicted_revenue));
            });
        }

        return {
            labels,
            datasets: [
                {
                    label: t('Historical Revenue'),
                    data: histData,
                    borderColor: '#4f46e5',
                    backgroundColor: 'rgba(79, 70, 229, 0.08)',
                    tension: 0.2,
                    fill: true,
                },
                {
                    label: t('30-Day Projected Revenue'),
                    data: predData,
                    borderColor: '#0284c7',
                    borderDash: [4, 4],
                    backgroundColor: 'rgba(2, 132, 199, 0.05)',
                    tension: 0.2,
                    fill: true,
                }
            ]
        };
    }, [analyticsData, t]);

    // ═════════════════════════════════════════════════════════════════════════
    // RENDER
    // ═════════════════════════════════════════════════════════════════════════
    return (
        <div className="space-y-6 pb-20 max-w-7xl mx-auto">

            {/* ── Page Header & Tabs ── */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-5">
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center font-black text-sm">
                                📊
                            </span>
                            <h1 className="text-2xl font-black text-slate-900 tracking-tight">
                                {t('Reports & Business Analytics')}
                            </h1>
                            <span className="bg-indigo-100 text-indigo-700 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full">
                                Hub
                            </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                            <span className="font-semibold text-slate-700">{branchName}</span> — {t('Unified management reporting: profitability, day-by-day cash flows, double-entry audit and stock health.')}
                        </p>
                    </div>

                    {/* Quick Print Action based on active tab */}
                    <div className="flex items-center gap-2">
                        {activeTab === 0 && (
                            <button
                                onClick={handlePrintTab1}
                                className="bg-slate-900 hover:bg-black text-white text-xs font-bold px-4 py-2 rounded-xl shadow-sm transition flex items-center gap-2"
                            >
                                🖨️ {t('Print Performance Report')}
                            </button>
                        )}
                        {activeTab === 1 && (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handlePrintDailyClosing}
                                    className="bg-slate-800 hover:bg-slate-900 text-white text-xs font-bold px-3 py-2 rounded-xl shadow-sm transition flex items-center gap-1.5"
                                >
                                    🖨️ {t('Print Z-Report')}
                                </button>
                                <button
                                    onClick={handlePrintMonthlyMatrix}
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-3 py-2 rounded-xl shadow-sm transition flex items-center gap-1.5"
                                >
                                    🖨️ {t('Print Monthly Matrix')}
                                </button>
                            </div>
                        )}
                        {activeTab === 2 && (
                            <button
                                onClick={handlePrintAudit}
                                className="bg-slate-900 hover:bg-black text-white text-xs font-bold px-4 py-2 rounded-xl shadow-sm transition flex items-center gap-2"
                            >
                                🖨️ {t('Print Audit & Alerts')}
                            </button>
                        )}
                    </div>
                </div>

                {/* ── 3 MASTER TABS ── */}
                <div className="flex gap-2 pt-4 border-b-2 border-slate-100 -mb-6 overflow-x-auto">
                    {[
                        { id: 0, label: t('Performance & Executive Overview'), icon: '📈', desc: t('Valuation, P&L, break-even, branch comparison') },
                        { id: 1, label: t('Daily Closing & Monthly Matrix'), icon: '🏦', desc: t('EOD cash drawer, closures, day-by-day matrix') },
                        { id: 2, label: t('Financial Audit & Alerts'), icon: '⚖️', desc: t('Ledger balance, journal entries, expiry alerts') },
                    ].map((tab) => {
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => switchTab(tab.id)}
                                className={`flex items-center gap-2.5 px-5 py-3 border-b-2 font-bold text-sm transition-all whitespace-nowrap cursor-pointer ${
                                    isActive
                                        ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50 rounded-t-xl'
                                        : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50 rounded-t-xl'
                                }`}
                            >
                                <span className="text-base">{tab.icon}</span>
                                <div className="text-left">
                                    <span className="block leading-none">{tab.label}</span>
                                    <span className="text-[10px] text-slate-400 font-normal mt-0.5 block">{tab.desc}</span>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ═════════════════════════════════════════════════════════════════ */}
            {/* TAB 0: EXECUTIVE & MULTI-BRANCH PERFORMANCE                     */}
            {/* ═════════════════════════════════════════════════════════════════ */}
            {activeTab === 0 && (
                <div className="space-y-6">
                    {/* Filter bar */}
                    <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div className="flex items-center gap-3 flex-wrap">
                            <div>
                                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{t('Branch Filter')}</label>
                                <select
                                    value={branchFilter}
                                    onChange={(e) => setBranchFilter(e.target.value)}
                                    className="border border-slate-300 rounded-lg px-3 py-1.5 text-xs font-semibold bg-white text-slate-800 outline-none focus:ring-2 focus:ring-indigo-400"
                                >
                                    <option value="All">{t('All Branches')}</option>
                                    {depos.map(d => (
                                        <option key={d.id} value={d.id}>{d.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{t('Period')}</label>
                                <select
                                    value={dateRangeType}
                                    onChange={(e) => setDateRangeType(e.target.value)}
                                    className="border border-slate-300 rounded-lg px-3 py-1.5 text-xs font-semibold bg-white text-slate-800 outline-none focus:ring-2 focus:ring-indigo-400"
                                >
                                    <option>{t('This Month')}</option>
                                    <option>{t('This Year')}</option>
                                    <option>{t('Custom')}</option>
                                </select>
                            </div>
                            {dateRangeType === 'Custom' && (
                                <div className="flex items-center gap-2">
                                    <div>
                                        <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{t('From')}</label>
                                        <input
                                            type="date"
                                            value={customStart}
                                            onChange={(e) => setCustomStart(e.target.value)}
                                            className="border border-slate-300 rounded-lg px-2 py-1 text-xs"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{t('To')}</label>
                                        <input
                                            type="date"
                                            value={customEnd}
                                            onChange={(e) => setCustomEnd(e.target.value)}
                                            className="border border-slate-300 rounded-lg px-2 py-1 text-xs"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={fetchPerformance}
                                className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold px-3 py-1.5 rounded-lg transition"
                            >
                                ↺ {t('Refresh')}
                            </button>
                        </div>
                    </div>

                    {perfLoading && (
                        <div className="p-16 text-center">
                            <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                            <p className="text-slate-500 font-semibold text-sm">{t('Consolidating financial & multi-branch data...')}</p>
                        </div>
                    )}

                    {!perfLoading && perfData?.executive_summary && (
                        <>
                            {/* KPI Grid */}
                            {(() => {
                                const ex = perfData.executive_summary;
                                const isPos = ex.net_profit >= 0;
                                return (
                                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                                        <div className="bg-white rounded-xl p-5 border border-slate-200 border-t-4 border-t-slate-500 shadow-sm">
                                            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">{t('Stock Cost (Capital)')}</p>
                                            <p className="text-2xl font-black text-slate-800">{money(ex.inventory_cost)} <span className="text-xs font-normal text-slate-400">CFA</span></p>
                                            <p className="text-[11px] text-slate-400 mt-1">{t('Capital invested in inventory')}</p>
                                        </div>
                                        <div className="bg-white rounded-xl p-5 border border-slate-200 border-t-4 border-t-blue-500 shadow-sm">
                                            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">{t('Stock Sell Value')}</p>
                                            <p className="text-2xl font-black text-blue-700">{money(ex.inventory_retail)} <span className="text-xs font-normal text-slate-400">CFA</span></p>
                                            <p className="text-[11px] text-blue-500 mt-1">+{money(ex.inventory_potential_profit)} {t('potential gain')}</p>
                                        </div>
                                        <div className="bg-white rounded-xl p-5 border border-slate-200 border-t-4 border-t-emerald-500 shadow-sm">
                                            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">{t('Revenue')}</p>
                                            <p className="text-2xl font-black text-emerald-700">{money(ex.sales_revenue)} <span className="text-xs font-normal text-slate-400">CFA</span></p>
                                            <p className="text-[11px] text-slate-400 mt-1">{t('Invoiced for selected period')}</p>
                                        </div>
                                        <div className="bg-white rounded-xl p-5 border border-slate-200 border-t-4 border-t-orange-500 shadow-sm">
                                            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">{t('Operating Expenses')}</p>
                                            <p className="text-2xl font-black text-orange-700">{money(ex.operating_expenses)} <span className="text-xs font-normal text-slate-400">CFA</span></p>
                                            <p className="text-[11px] text-slate-400 mt-1">{t('Depenses and overheads')}</p>
                                        </div>
                                        <div className={`bg-white rounded-xl p-5 border border-slate-200 border-t-4 ${isPos ? 'border-t-green-500' : 'border-t-red-500'} shadow-sm`}>
                                            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">{t('Net Profit / Loss')}</p>
                                            <p className={`text-2xl font-black ${isPos ? 'text-green-700' : 'text-red-700'}`}>
                                                {isPos ? '+' : ''}{money(ex.net_profit)} <span className="text-xs font-normal text-slate-400">CFA</span>
                                            </p>
                                            <p className="text-[11px] font-bold mt-1 text-slate-500">{t('Margin')}: {ex.profit_margin.toFixed(1)}%</p>
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* Two-Column: Profit & Loss Statement + Cost Recovery Progress */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* P&L summary */}
                                <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                                    <h3 className="text-base font-black text-slate-900 mb-4 flex items-center gap-2">
                                        📊 {t('Consolidated Income Statement (P&L)')}
                                    </h3>
                                    <div className="space-y-2.5 text-sm">
                                        <div className="flex justify-between py-2 border-b border-slate-100">
                                            <span className="text-slate-600 font-medium">{t('Gross Invoiced Sales')}</span>
                                            <span className="font-bold text-slate-900">{money(perfData.executive_summary.sales_revenue)} CFA</span>
                                        </div>
                                        <div className="flex justify-between py-2 border-b border-slate-100">
                                            <span className="text-slate-600 font-medium">{t('Cost of Goods Sold (COGS)')}</span>
                                            <span className="font-bold text-red-600">-{money(perfData.executive_summary.cogs)} CFA</span>
                                        </div>
                                        <div className="flex justify-between py-2.5 px-3 rounded-lg bg-emerald-50 text-emerald-900">
                                            <span className="font-bold">{t('Gross Operating Profit')}</span>
                                            <span className="font-black text-base">{money(perfData.executive_summary.gross_profit)} CFA</span>
                                        </div>
                                        <div className="flex justify-between py-2 border-b border-slate-100">
                                            <span className="text-slate-600 font-medium">{t('Operating Expenses (Depenses)')}</span>
                                            <span className="font-bold text-orange-600">-{money(perfData.executive_summary.operating_expenses)} CFA</span>
                                        </div>
                                        <div className={`flex justify-between py-3 px-4 rounded-xl text-white shadow-md ${perfData.executive_summary.net_profit >= 0 ? 'bg-slate-900' : 'bg-red-700'}`}>
                                            <div>
                                                <span className="font-black text-base block">{t('NET PERIOD PROFIT')}</span>
                                                <span className="text-[10px] opacity-75">{t('Profit margin')}: {perfData.executive_summary.profit_margin.toFixed(1)}%</span>
                                            </div>
                                            <span className="font-black text-2xl">
                                                {perfData.executive_summary.net_profit >= 0 ? '+' : ''}{money(perfData.executive_summary.net_profit)} CFA
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Cost Recovery & Break-even */}
                                <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex flex-col justify-between">
                                    <div>
                                        <h3 className="text-base font-black text-slate-900 mb-1 flex items-center gap-2">
                                            🎯 {t('Inventory Cost Recovery & Break-Even')}
                                        </h3>
                                        <p className="text-xs text-slate-400 mb-5">{t('Tracking whether initial inventory capital has been fully recuperated through sales.')}</p>

                                        {perfData.cost_recovery && (
                                            <div className="space-y-3 text-sm">
                                                <div className="flex justify-between border-b border-slate-100 pb-2">
                                                    <span className="text-slate-600">{t('Total Inventory Investment')}</span>
                                                    <span className="font-bold">{money(perfData.cost_recovery.initial_investment)} CFA</span>
                                                </div>
                                                <div className="flex justify-between border-b border-slate-100 pb-2">
                                                    <span className="text-slate-600">{t('Recovered from Sales (COGS)')}</span>
                                                    <span className="font-bold text-emerald-600">{money(perfData.cost_recovery.recovered_from_sales)} CFA</span>
                                                </div>
                                                <div className="flex justify-between border-b border-slate-100 pb-2">
                                                    <span className="text-slate-600">{t('Remaining to Recover')}</span>
                                                    <span className="font-bold text-red-600">{money(perfData.cost_recovery.remaining_to_recover)} CFA</span>
                                                </div>

                                                <div className="pt-2">
                                                    <div className="flex justify-between items-center mb-1 text-xs font-bold">
                                                        <span className="text-slate-600">{t('Recovery Percentage')}</span>
                                                        <span className="text-indigo-600 text-lg font-black">{perfData.cost_recovery.recovery_percentage.toFixed(1)}%</span>
                                                    </div>
                                                    <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden">
                                                        <div
                                                            className="h-full bg-indigo-600 rounded-full transition-all duration-700"
                                                            style={{ width: `${Math.min(100, perfData.cost_recovery.recovery_percentage)}%` }}
                                                        ></div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className={`mt-5 p-4 rounded-xl border flex items-center gap-3 text-xs ${perfData.cost_recovery?.fully_recovered ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                                        <span className="text-2xl">{perfData.cost_recovery?.fully_recovered ? '✓' : '⏳'}</span>
                                        <div>
                                            <p className="font-bold text-sm">
                                                {perfData.cost_recovery?.fully_recovered ? t('Capital Fully Recovered!') : t('Capital in Recovery Process')}
                                            </p>
                                            <p className="mt-0.5">
                                                {perfData.cost_recovery?.fully_recovered
                                                    ? t('All subsequent sales generate pure gross margin.')
                                                    : t('Need {{amt}} CFA more in cost recovery before original investment is completely paid off.', { amt: money(perfData.cost_recovery?.remaining_to_recover) })}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Branch Performance Table */}
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                                <div className="p-5 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
                                    <div>
                                        <h3 className="text-base font-black text-slate-800">{t('Branch-by-Branch Breakdown')}</h3>
                                        <p className="text-xs text-slate-500">{t('Stock valuation, revenue, and individual profitability per depot.')}</p>
                                    </div>
                                    <span className="text-xs font-bold text-slate-400">
                                        {perfData.branch_breakdown?.length || 0} {t('Locations')}
                                    </span>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="bg-slate-100 text-slate-600 text-xs uppercase font-bold border-b border-slate-200">
                                                <th className="px-4 py-3 text-left">{t('Branch')}</th>
                                                <th className="px-4 py-3 text-right">{t('Stock Cost')}</th>
                                                <th className="px-4 py-3 text-right">{t('Retail Value')}</th>
                                                <th className="px-4 py-3 text-right">{t('Sales')}</th>
                                                <th className="px-4 py-3 text-right">{t('COGS')}</th>
                                                <th className="px-4 py-3 text-right">{t('Expenses')}</th>
                                                <th className="px-4 py-3 text-right">{t('Net Profit')}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {perfData.branch_breakdown?.map((b) => {
                                                const isLoss = b.net_profit < 0;
                                                return (
                                                    <tr key={b.branch_id} className={`hover:bg-slate-50 transition ${isLoss ? 'bg-red-50/20' : ''}`}>
                                                        <td className="px-4 py-3 font-bold text-slate-800">{b.branch_name}</td>
                                                        <td className="px-4 py-3 text-right text-slate-600">{money(b.stock_cost)}</td>
                                                        <td className="px-4 py-3 text-right text-blue-600 font-medium">{money(b.stock_retail)}</td>
                                                        <td className="px-4 py-3 text-right text-slate-800 font-semibold">{money(b.sales)}</td>
                                                        <td className="px-4 py-3 text-right text-red-500">{money(b.cogs)}</td>
                                                        <td className="px-4 py-3 text-right text-orange-500">{money(b.expenses)}</td>
                                                        <td className={`px-4 py-3 text-right font-black ${isLoss ? 'text-red-600' : 'text-emerald-700'}`}>
                                                            {b.net_profit > 0 ? '+' : ''}{money(b.net_profit)} CFA
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                        <tfoot>
                                            <tr className="bg-slate-900 text-white font-bold text-xs uppercase">
                                                <td className="px-4 py-3">{t('NETWORK TOTAL')}</td>
                                                <td className="px-4 py-3 text-right">{money(perfData.executive_summary.inventory_cost)}</td>
                                                <td className="px-4 py-3 text-right">{money(perfData.executive_summary.inventory_retail)}</td>
                                                <td className="px-4 py-3 text-right">{money(perfData.executive_summary.sales_revenue)}</td>
                                                <td className="px-4 py-3 text-right">{money(perfData.executive_summary.cogs)}</td>
                                                <td className="px-4 py-3 text-right">{money(perfData.executive_summary.operating_expenses)}</td>
                                                <td className={`px-4 py-3 text-right text-sm ${perfData.executive_summary.net_profit >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                                                    {perfData.executive_summary.net_profit > 0 ? '+' : ''}{money(perfData.executive_summary.net_profit)} CFA
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>

                            {/* Forecasting Chart & Top Products (from Business Intelligence) */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {forecastChartData && (
                                    <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                                        <h3 className="text-base font-bold text-slate-900 mb-1">{t('30-Day Revenue Trend & Projections')}</h3>
                                        <p className="text-xs text-slate-400 mb-4">{t('Historical daily sales plotted against intelligent run-rate forecast.')}</p>
                                        <div className="h-64">
                                            <Line data={forecastChartData} options={{ maintainAspectRatio: false }} />
                                        </div>
                                    </div>
                                )}

                                {biData?.top_contributors && (
                                    <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                                        <h3 className="text-base font-bold text-slate-900 mb-1">{t('Top Profit Contributing Products')}</h3>
                                        <p className="text-xs text-slate-400 mb-4">{t('Products driving the highest gross profit margins.')}</p>
                                        <div className="space-y-3">
                                            {biData.top_contributors.slice(0, 5).map((item, i) => (
                                                <div key={i} className="flex items-center gap-3">
                                                    <span className="w-6 h-6 rounded-md bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600">
                                                        {i + 1}
                                                    </span>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-xs font-semibold text-slate-800 truncate">{item.name}</p>
                                                        <div className="h-1.5 bg-slate-100 rounded-full mt-1 overflow-hidden">
                                                            <div
                                                                className="h-full bg-indigo-600 rounded-full"
                                                                style={{ width: `${(item.profit_contribution / (biData.top_contributors[0]?.profit_contribution || 1)) * 100}%` }}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                    <span className="text-xs font-bold text-indigo-600">{money(item.profit_contribution)} CFA</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {!perfLoading && !perfData?.executive_summary && (
                        <div className="bg-white rounded-2xl p-12 text-center border border-slate-200 shadow-sm">
                            <div className="w-16 h-16 bg-slate-100 text-slate-400 rounded-full flex items-center justify-center text-2xl mx-auto mb-4">
                                📊
                            </div>
                            <h3 className="text-base font-bold text-slate-800 mb-1">{t('No Performance Data Available')}</h3>
                            <p className="text-xs text-slate-500 max-w-md mx-auto mb-6">
                                {perfError || t('We could not retrieve performance data for the selected branch and date range.')}
                            </p>
                            <button
                                onClick={fetchPerformance}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-4 py-2 rounded-xl transition shadow"
                            >
                                ↺ {t('Try Again')}
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* ═════════════════════════════════════════════════════════════════ */}
            {/* TAB 1: DAILY CLOSING & MONTHLY MATRIX                           */}
            {/* ═════════════════════════════════════════════════════════════════ */}
            {activeTab === 1 && (
                <div className="space-y-6">
                    {/* Controls row */}
                    <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div className="flex items-center gap-3 flex-wrap">
                            <div>
                                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{t('Target Business Day')}</label>
                                <input
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => {
                                        setSelectedDate(e.target.value);
                                        const d = new Date(e.target.value);
                                        setSelectedMonth(d.getMonth() + 1);
                                        setSelectedYear(d.getFullYear());
                                    }}
                                    className="border border-slate-300 rounded-lg px-3 py-1.5 text-xs font-semibold bg-white text-slate-800 outline-none focus:ring-2 focus:ring-indigo-400"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{t('Matrix Month')}</label>
                                <select
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                                    className="border border-slate-300 rounded-lg px-3 py-1.5 text-xs font-semibold bg-white text-slate-800 outline-none focus:ring-2 focus:ring-indigo-400"
                                >
                                    {MONTH_NAMES.map((m, i) => (
                                        <option key={i} value={i + 1}>{m}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{t('Matrix Year')}</label>
                                <input
                                    type="number"
                                    value={selectedYear}
                                    min="2020"
                                    max="2099"
                                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                                    className="border border-slate-300 rounded-lg px-2 py-1.5 text-xs font-semibold w-20 bg-white text-slate-800 outline-none"
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            {closureStatus === 'Open' && (
                                <>
                                    <button
                                        onClick={() => setExpenseModalOpen(true)}
                                        className="bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold px-3.5 py-2 rounded-xl transition shadow-sm"
                                    >
                                        + {t('Log Expense')}
                                    </button>
                                    <button
                                        onClick={() => setCloseModalOpen(true)}
                                        className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-4 py-2 rounded-xl transition shadow-sm flex items-center gap-1.5"
                                    >
                                        🔒 {t('Close Day (Lock)')}
                                    </button>
                                </>
                            )}
                            {closureStatus === 'Closed' && (
                                <span className="bg-red-100 text-red-700 text-xs font-black px-4 py-2 rounded-xl border border-red-200 flex items-center gap-1.5">
                                    🔒 {t('Day Closed & Locked')}
                                </span>
                            )}
                        </div>
                    </div>

                    {matrixLoading && (
                        <div className="p-16 text-center">
                            <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                            <p className="text-slate-500 font-semibold text-sm">{t('Loading live EOD closing & matrix records...')}</p>
                        </div>
                    )}

                    {!matrixLoading && (
                        <>
                            {/* Live EOD Snapshot Banner */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="bg-white rounded-xl p-4 border border-slate-200 border-t-4 border-t-blue-500 shadow-sm">
                                    <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">{t('Cash Sales Today')}</p>
                                    <p className="text-2xl font-black text-blue-700">{money(closureSummary?.cash_total)} CFA</p>
                                    <p className="text-[10px] text-slate-400 mt-1">{closureSummary?.invoice_count || 0} {t('invoices')}</p>
                                </div>
                                <div className="bg-white rounded-xl p-4 border border-slate-200 border-t-4 border-t-purple-500 shadow-sm">
                                    <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">{t('Card / Visa Today')}</p>
                                    <p className="text-2xl font-black text-purple-700">{money(closureSummary?.card_total)} CFA</p>
                                    <p className="text-[10px] text-slate-400 mt-1">{t('Electronic payments')}</p>
                                </div>
                                <div className="bg-white rounded-xl p-4 border border-slate-200 border-t-4 border-t-orange-400 shadow-sm">
                                    <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">{t('Daily Expenses')}</p>
                                    <p className="text-2xl font-black text-orange-700">{money(closureSummary?.expense_total)} CFA</p>
                                    <p className="text-[10px] text-slate-400 mt-1">{dailyExpenses.length} {t('recorded items')}</p>
                                </div>
                                <div className="bg-white rounded-xl p-4 border border-slate-200 border-t-4 border-t-emerald-500 shadow-sm">
                                    <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">{t('Expected Physical Cash')}</p>
                                    <p className="text-2xl font-black text-emerald-700">
                                        {money(closureSummary?.expected_cash ?? (closureSummary?.cash_total - (closureSummary?.expense_total || 0)))} CFA
                                    </p>
                                    <p className="text-[10px] text-slate-400 mt-1">= Cash Collected − Depenses</p>
                                </div>
                            </div>

                            {/* Monthly Matrix Day-by-Day Table */}
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                                <div className="p-5 bg-slate-50 border-b border-slate-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                                    <div>
                                        <h3 className="text-base font-black text-slate-800">
                                            📅 {t('Monthly Sales & Outflow Matrix')} — {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
                                        </h3>
                                        <p className="text-xs text-slate-500">{t('Day-by-day cash sales, visa/card, credit, purchases, and net daily cash flow.')}</p>
                                    </div>
                                    <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700">
                                        {matrixRows.length} {t('Calendar Days')}
                                    </span>
                                </div>

                                <div className="overflow-x-auto">
                                    <table className="w-full text-xs">
                                        <thead>
                                            <tr className="bg-slate-800 text-white font-bold uppercase">
                                                <th className="px-3.5 py-3 text-left">{t('Date')}</th>
                                                <th className="px-3 py-3 text-right">{t('Cash')}</th>
                                                <th className="px-3 py-3 text-right">{t('Visa/Card')}</th>
                                                <th className="px-3 py-3 text-right">{t('Credit')}</th>
                                                <th className="px-3 py-3 text-right">{t('Total Sales')}</th>
                                                <th className="px-3 py-3 text-right">{t('Achats')}</th>
                                                <th className="px-3 py-3 text-right">{t('Depenses')}</th>
                                                <th className="px-3 py-3 text-right">{t('Net Cash Flow')}</th>
                                                <th className="px-3 py-3 text-center">{t('Status')}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {matrixRows.map((r) => {
                                                const hasActivity = num(r.total_daily_sales) > 0 || num(r.accepted_achats) > 0 || num(r.depenses) > 0;
                                                const isNetPos = num(r.net_daily_cash_flow) >= 0;
                                                const isSelected = r.date === selectedDate;
                                                return (
                                                    <tr
                                                        key={r.date}
                                                        onClick={() => setSelectedDate(r.date)}
                                                        className={`transition cursor-pointer ${
                                                            isSelected ? 'bg-indigo-50 font-semibold' : hasActivity ? 'hover:bg-slate-50' : 'text-slate-400 bg-slate-50/40'
                                                        }`}
                                                    >
                                                        <td className="px-3.5 py-2.5 whitespace-nowrap text-slate-800 font-medium">
                                                            {r.date}
                                                            {isSelected && <span className="ml-1 text-[9px] bg-indigo-600 text-white px-1.5 py-0.2 rounded font-bold">Selected</span>}
                                                        </td>
                                                        <td className="px-3 py-2.5 text-right">{hasActivity ? money(r.cash_sales) : '—'}</td>
                                                        <td className="px-3 py-2.5 text-right">{hasActivity ? money(r.visa_sales) : '—'}</td>
                                                        <td className="px-3 py-2.5 text-right">{hasActivity ? money(r.credit_sales) : '—'}</td>
                                                        <td className="px-3 py-2.5 text-right font-bold text-blue-600">{hasActivity ? money(r.total_daily_sales) : '—'}</td>
                                                        <td className="px-3 py-2.5 text-right text-red-500">{hasActivity ? money(r.accepted_achats) : '—'}</td>
                                                        <td className="px-3 py-2.5 text-right text-orange-500">{hasActivity ? money(r.depenses) : '—'}</td>
                                                        <td className={`px-3 py-2.5 text-right font-black ${isNetPos ? 'text-green-700' : 'text-red-700'}`}>
                                                            {hasActivity ? `${isNetPos ? '+' : ''}${money(r.net_daily_cash_flow)}` : '—'}
                                                        </td>
                                                        <td className="px-3 py-2.5 text-center">
                                                            {r.day_status && (
                                                                <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${
                                                                    r.day_status === 'Closed' ? 'bg-slate-200 text-slate-700' : 'bg-green-100 text-green-700'
                                                                }`}>
                                                                    {r.day_status === 'Closed' ? '🔒 Closed' : '🟢 Open'}
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                        <tfoot>
                                            <tr className="bg-indigo-900 text-white font-bold text-xs uppercase">
                                                <td className="px-3.5 py-3.5">{t('MONTH TOTAL')}</td>
                                                <td className="px-3 py-3.5 text-right">{money(matrixTotals.cash_sales)}</td>
                                                <td className="px-3 py-3.5 text-right">{money(matrixTotals.visa_sales)}</td>
                                                <td className="px-3 py-3.5 text-right">{money(matrixTotals.credit_sales)}</td>
                                                <td className="px-3 py-3.5 text-right text-blue-200">{money(matrixTotals.total_sales)}</td>
                                                <td className="px-3 py-3.5 text-right text-red-300">{money(matrixTotals.accepted_achats)}</td>
                                                <td className="px-3 py-3.5 text-right text-orange-300">{money(matrixTotals.depenses)}</td>
                                                <td className={`px-3 py-3.5 text-right text-sm font-black ${matrixTotals.net_cash_flow >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                                                    {matrixTotals.net_cash_flow >= 0 ? '+' : ''}{money(matrixTotals.net_cash_flow)} CFA
                                                </td>
                                                <td></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* ═════════════════════════════════════════════════════════════════ */}
            {/* TAB 2: FINANCIAL AUDIT & ALERTS                                 */}
            {/* ═════════════════════════════════════════════════════════════════ */}
            {activeTab === 2 && (
                <div className="space-y-6">
                    {/* Controls row */}
                    <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div className="flex items-center gap-3 flex-wrap">
                            <div>
                                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{t('Audit Period (From)')}</label>
                                <input
                                    type="date"
                                    value={auditStart}
                                    onChange={(e) => setAuditStart(e.target.value)}
                                    className="border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-semibold bg-white text-slate-800 outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{t('Audit Period (To)')}</label>
                                <input
                                    type="date"
                                    value={auditEnd}
                                    onChange={(e) => setAuditEnd(e.target.value)}
                                    className="border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-semibold bg-white text-slate-800 outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{t('Expiry Alert Window')}</label>
                                <select
                                    value={expiryDays}
                                    onChange={(e) => setExpiryDays(e.target.value)}
                                    className="border border-slate-300 rounded-lg px-3 py-1.5 text-xs font-semibold bg-white text-slate-800 outline-none"
                                >
                                    <option value="7">{t('Next 7 days')}</option>
                                    <option value="30">{t('Next 30 days')}</option>
                                    <option value="60">{t('Next 60 days')}</option>
                                    <option value="90">{t('Next 90 days')}</option>
                                </select>
                            </div>
                        </div>

                        <button
                            onClick={fetchAuditAndAlerts}
                            className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold px-3 py-1.5 rounded-lg transition"
                        >
                            ↺ {t('Run Audit')}
                        </button>
                    </div>

                    {auditLoading && (
                        <div className="p-16 text-center">
                            <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                            <p className="text-slate-500 font-semibold text-sm">{t('Auditing double-entry ledger & scanning stock expiries...')}</p>
                        </div>
                    )}

                    {!auditLoading && auditData && (
                        <>
                            {/* Verification Banner */}
                            {(() => {
                                const deb = num(auditData.totals?.debit);
                                const cred = num(auditData.totals?.credit);
                                const diff = deb - cred;
                                const isBalanced = Math.abs(diff) < 0.01;
                                return (
                                    <div className={`p-5 rounded-2xl border flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 ${
                                        isBalanced ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-red-50 border-red-200 text-red-900'
                                    }`}>
                                        <div className="flex items-center gap-3">
                                            <span className="text-3xl">{isBalanced ? '⚖️' : '⚠️'}</span>
                                            <div>
                                                <h3 className="font-black text-lg">
                                                    {isBalanced ? t('Double-Entry Ledger is Balanced') : t('Ledger Variance Detected!')}
                                                </h3>
                                                <p className="text-xs opacity-80">
                                                    {isBalanced
                                                        ? t('Debit and Credit entries perfectly reconcile across all accounts.')
                                                        : t('Difference: {{diff}} CFA between Debit and Credit entries.', { diff: money(Math.abs(diff)) })}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4 text-xs font-bold">
                                            <div className="bg-white/70 px-3 py-1.5 rounded-lg border">
                                                <span className="text-slate-500 uppercase block text-[9px]">{t('Total Debit')}</span>
                                                <span className="text-sm font-black text-rose-700">{money(deb)} CFA</span>
                                            </div>
                                            <div className="bg-white/70 px-3 py-1.5 rounded-lg border">
                                                <span className="text-slate-500 uppercase block text-[9px]">{t('Total Credit')}</span>
                                                <span className="text-sm font-black text-emerald-700">{money(cred)} CFA</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* Cash on Hand & Owner Capital */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
                                    <p className="text-[11px] font-bold uppercase text-slate-500 mb-1">{t('Total Cash on Hand (Real-Time Liquidity)')}</p>
                                    <p className="text-3xl font-black text-slate-900">{money(auditData.summary?.cash_balance)} <span className="text-sm text-slate-400 font-normal">CFA</span></p>
                                    <p className="text-xs text-slate-400 mt-1">{t('Aggregated cash balance across all registers and accounts.')}</p>
                                </div>
                                <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
                                    <p className="text-[11px] font-bold uppercase text-slate-500 mb-1">{t('Owner Capital / Equity')}</p>
                                    <p className="text-3xl font-black text-indigo-700">{money(auditData.summary?.capital_balance)} <span className="text-sm text-slate-400 font-normal">CFA</span></p>
                                    <p className="text-xs text-slate-400 mt-1">{t('Documented equity investment held in company.')}</p>
                                </div>
                            </div>

                            {/* Expiry Alerts Summary Chips */}
                            <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
                                <div className="flex justify-between items-center mb-3">
                                    <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
                                        🚨 {t('Stock Expiry Watchdog')}
                                        <span className="text-xs text-slate-400 font-normal">({t('Next {{days}} days', { days: expiryDays })})</span>
                                    </h3>
                                    <span className="text-xs font-bold text-slate-500">{expiryItems.length} {t('monitored batches')}</span>
                                </div>
                                <div className="flex gap-3 flex-wrap">
                                    {expiryCounts.expired > 0 && (
                                        <div className="bg-red-500 text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm">
                                            🚨 {expiryCounts.expired} {t('Expired')}
                                        </div>
                                    )}
                                    {expiryCounts.critical > 0 && (
                                        <div className="bg-rose-500 text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm">
                                            🔴 {expiryCounts.critical} {t('Critical (<7 days)')}
                                        </div>
                                    )}
                                    {expiryCounts.soon > 0 && (
                                        <div className="bg-orange-500 text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm">
                                            🟠 {expiryCounts.soon} {t('Expiring Soon (<30 days)')}
                                        </div>
                                    )}
                                    {expiryCounts.watch > 0 && (
                                        <div className="bg-amber-500 text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm">
                                            🟡 {expiryCounts.watch} {t('Watch')}
                                        </div>
                                    )}
                                    {expiryItems.length === 0 && (
                                        <div className="bg-emerald-50 text-emerald-800 border border-emerald-200 px-3 py-1.5 rounded-lg text-xs font-bold">
                                            ✓ {t('No items expiring within this window.')}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Categorized Spending Breakdown */}
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                                <div className="p-4 bg-slate-50 border-b border-slate-200">
                                    <h3 className="text-sm font-black text-slate-900">{t('Categorized Spending & Earnings Audit')}</h3>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-xs">
                                        <thead>
                                            <tr className="bg-slate-100 text-slate-600 font-bold uppercase">
                                                <th className="px-4 py-2.5 text-left">{t('Category')}</th>
                                                <th className="px-4 py-2.5 text-left">{t('Type')}</th>
                                                <th className="px-4 py-2.5 text-right">{t('Entries')}</th>
                                                <th className="px-4 py-2.5 text-right">{t('Debit')}</th>
                                                <th className="px-4 py-2.5 text-right">{t('Credit')}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {Object.entries(auditData.categories || {}).map(([catName, catData]) => (
                                                <tr key={catName} className="hover:bg-slate-50">
                                                    <td className="px-4 py-2.5 font-bold text-slate-800">{catName}</td>
                                                    <td className="px-4 py-2.5 text-slate-500">{catData.type}</td>
                                                    <td className="px-4 py-2.5 text-right text-slate-600">{catData.items?.length || 0}</td>
                                                    <td className="px-4 py-2.5 text-right font-medium text-rose-600">{money(catData.subtotal_debit)}</td>
                                                    <td className="px-4 py-2.5 text-right font-medium text-emerald-600">{money(catData.subtotal_credit)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* ═════════════════════════════════════════════════════════════════ */}
            {/* MODALS: CLOSE DAY CONFIRMATION                                  */}
            {/* ═════════════════════════════════════════════════════════════════ */}
            {closeModalOpen && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
                        <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-900 text-white">
                            <h3 className="text-base font-black flex items-center gap-2">
                                🔒 {t('Lock & Close Day')} ({selectedDate})
                            </h3>
                            <button onClick={() => setCloseModalOpen(false)} className="text-slate-400 hover:text-white text-xl">✕</button>
                        </div>
                        <form onSubmit={handleConfirmCloseDay} className="p-6 space-y-4 text-xs">
                            <p className="text-red-600 font-bold">
                                ⚠️ {t('Warning: Locking this business date prevents any further invoices, purchases or edits for this day.')}
                            </p>
                            <div className="bg-slate-50 p-3 rounded-xl space-y-1">
                                <div className="flex justify-between">
                                    <span className="text-slate-500">{t('Total Cash Collected')}:</span>
                                    <span className="font-bold">{money(closureSummary?.cash_total)} CFA</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-500">{t('Total Daily Expenses')}:</span>
                                    <span className="font-bold text-red-600">-{money(closureSummary?.expense_total)} CFA</span>
                                </div>
                                <div className="flex justify-between border-t border-slate-200 pt-1 font-black text-slate-800">
                                    <span>{t('Expected Cash in Drawer')}:</span>
                                    <span className="text-emerald-700">
                                        {money(closureSummary?.expected_cash ?? (closureSummary?.cash_total - (closureSummary?.expense_total || 0)))} CFA
                                    </span>
                                </div>
                            </div>

                            <div>
                                <label className="block font-bold text-slate-700 mb-1 uppercase">{t('Actual Physical Cash Count (CFA)')} *</label>
                                <input
                                    type="number"
                                    required
                                    value={actualCashCount}
                                    onChange={(e) => setActualCashCount(e.target.value)}
                                    placeholder={money(closureSummary?.expected_cash ?? (closureSummary?.cash_total - (closureSummary?.expense_total || 0)))}
                                    className="w-full border border-slate-300 rounded-lg p-2.5 text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-indigo-400"
                                />
                            </div>

                            <div>
                                <label className="block font-bold text-slate-700 mb-1 uppercase">{t('Closing Notes / Variance Reason')}</label>
                                <textarea
                                    value={closingNote}
                                    onChange={(e) => setClosingNote(e.target.value)}
                                    rows={2}
                                    className="w-full border border-slate-300 rounded-lg p-2 text-xs outline-none focus:ring-2 focus:ring-indigo-400"
                                    placeholder={t('Optional notes regarding cash count or day operations')}
                                />
                            </div>

                            <div className="flex justify-end gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setCloseModalOpen(false)}
                                    className="px-4 py-2 border rounded-lg font-bold text-slate-600 hover:bg-slate-50"
                                >
                                    {t('Cancel')}
                                </button>
                                <button
                                    type="submit"
                                    disabled={closingSubmitting}
                                    className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-black transition disabled:opacity-50"
                                >
                                    {closingSubmitting ? t('Locking...') : t('Lock & Seal Day')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ═════════════════════════════════════════════════════════════════ */}
            {/* MODALS: LOG MANUAL EXPENSE                                      */}
            {/* ═════════════════════════════════════════════════════════════════ */}
            {expenseModalOpen && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
                        <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-900 text-white">
                            <h3 className="text-base font-black">+ {t('Log Operational Expense')}</h3>
                            <button onClick={() => setExpenseModalOpen(false)} className="text-slate-400 hover:text-white text-xl">✕</button>
                        </div>
                        <form onSubmit={handleLogExpense} className="p-6 space-y-4 text-xs">
                            <div>
                                <label className="block font-bold text-slate-700 mb-1 uppercase">{t('Category')} *</label>
                                <input
                                    type="text"
                                    required
                                    placeholder={t('e.g. Supplies, Transport, Electricity, Cleaning...')}
                                    value={expenseForm.category}
                                    onChange={(e) => setExpenseForm(f => ({ ...f, category: e.target.value }))}
                                    className="w-full border border-slate-300 rounded-lg p-2.5 text-xs outline-none focus:ring-2 focus:ring-indigo-400"
                                />
                            </div>
                            <div>
                                <label className="block font-bold text-slate-700 mb-1 uppercase">{t('Amount (CFA)')} *</label>
                                <input
                                    type="number"
                                    required
                                    min="1"
                                    value={expenseForm.amount}
                                    onChange={(e) => setExpenseForm(f => ({ ...f, amount: e.target.value }))}
                                    className="w-full border border-slate-300 rounded-lg p-2.5 text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-indigo-400"
                                />
                            </div>
                            <div>
                                <label className="block font-bold text-slate-700 mb-1 uppercase">{t('Description / Vendor')}</label>
                                <textarea
                                    value={expenseForm.description}
                                    onChange={(e) => setExpenseForm(f => ({ ...f, description: e.target.value }))}
                                    rows={2}
                                    className="w-full border border-slate-300 rounded-lg p-2 text-xs outline-none focus:ring-2 focus:ring-indigo-400"
                                />
                            </div>

                            <div className="p-3 bg-amber-50 rounded-lg text-amber-800 text-[11px] border border-amber-200 font-medium">
                                💡 {t('This expense will be deducted from today\'s physical cash drawer and logged in the accounting journal.')}
                            </div>

                            <div className="flex justify-end gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setExpenseModalOpen(false)}
                                    className="px-4 py-2 border rounded-lg font-bold text-slate-600 hover:bg-slate-50"
                                >
                                    {t('Cancel')}
                                </button>
                                <button
                                    type="submit"
                                    className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-bold transition"
                                >
                                    {t('Save Expense')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

        </div>
    );
}
